"use client";

import { ArrowRight, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuthCtaLink } from "./auth-cta-link";
import { cn } from "@/lib/utils";
import type { CreditMathConfig } from "@/config/counties/types";
import { trackFbLead } from "@/lib/analytics/facebook-pixel";

type CreditMathSectionProps = {
  data: CreditMathConfig;
};

export default function CreditMathSection({ data }: CreditMathSectionProps) {
  return (
    <section id="credit-math" className="bg-surface-alt scroll-mt-20 py-16 sm:py-24">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16 max-w-3xl mx-auto">
          <h2 className="text-[26px] md:text-[36px] lg:text-[45px] font-bold text-ink mb-6">
            {data.title}
          </h2>
          <p className="text-[14px] md:text-[16px] lg:text-[18px] text-muted-ink">
            {data.subtitle}
          </p>
        </div>

        <div className="mb-4 text-center text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-ink">
          {data.scenarioLabel}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch">
          {data.columns.map((col) => (
            <div
              key={col.name}
              className={cn(
                "flex flex-col rounded-2xl border bg-card p-6 sm:p-7",
                col.highlight
                  ? "border-primary shadow-xl shadow-primary/20 ring-1 ring-primary"
                  : "border-line"
              )}
            >
              <h3 className="text-lg font-bold text-ink">{col.name}</h3>
              <div className="mt-1 text-sm font-medium text-primary">{col.price}</div>
              <div className="mt-4 pt-4 border-t border-line-soft text-sm text-muted-ink">
                {col.allowance}
              </div>

              <ul className="mt-4 space-y-2.5 flex-grow">
                {col.caveats.map((caveat, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-ink">
                    {col.highlight ? (
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#22C55E]" />
                    ) : (
                      <X className="mt-0.5 h-4 w-4 shrink-0 text-[#EF4444]" />
                    )}
                    <span>{caveat}</span>
                  </li>
                ))}
              </ul>

              <div
                className={cn(
                  "mt-6 pt-5 border-t text-base font-bold",
                  col.highlight
                    ? "border-primary/20 text-primary"
                    : "border-line-soft text-ink"
                )}
              >
                {col.monthlyTotal}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <AuthCtaLink
            href="/dashboard"
            className="inline-block hover:scale-105 transition-transform duration-200"
            onClick={() => trackFbLead()}
          >
            <Button
              size="lg"
              className="group btn-ripple press rounded-lg px-8 py-7 bg-primary-surface text-primary-foreground text-[14px] lg:text-[16px] font-medium flex items-center justify-center gap-2 hover:bg-primary-surface/90"
            >
              {data.ctaText}
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Button>
          </AuthCtaLink>
        </div>

        <p className="mt-8 mx-auto max-w-3xl text-center text-xs text-subtle-ink leading-relaxed">
          {data.footnote}
        </p>
      </div>
    </section>
  );
}
